import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { RealtimeStatus } from '@/features/notifications/api/use-realtime-notifications';

export interface RealtimeStatusBadgeProps {
  status: RealtimeStatus;
  lastEventAt?: string | null;
  className?: string;
}

/**
 * RealtimeStatusBadge (W6-F5-S3) — small dot indicator next to the bell.
 *   green   → connected
 *   yellow  → reconnecting / connecting (transient)
 *   red     → disconnected (gave up after retries)
 *
 * The tooltip surfaces the last update time so a user that suspects the
 * bell is stale can confirm what they're looking at.
 */
export function RealtimeStatusBadge({
  status,
  lastEventAt,
  className,
}: RealtimeStatusBadgeProps): JSX.Element {
  const { t } = useTranslation();
  const colorClass =
    status === 'connected'
      ? 'bg-emerald-500'
      : status === 'reconnecting' || status === 'connecting'
        ? 'bg-amber-500 animate-pulse'
        : 'bg-rose-500';

  const labelKey =
    status === 'connected'
      ? 'notifications.realtime.live'
      : status === 'reconnecting' || status === 'connecting'
        ? 'notifications.realtime.reconnecting'
        : 'notifications.realtime.offline';

  const label = t(labelKey);
  const lastSeen = lastEventAt
    ? t('notifications.realtime.lastUpdate', {
        time: new Date(lastEventAt).toLocaleTimeString(),
      })
    : t('notifications.realtime.noUpdates');

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="realtime-status"
            data-status={status}
            aria-label={label}
            className={cn(
              'inline-block h-2 w-2 rounded-full',
              colorClass,
              className,
            )}
          />
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <div className="font-medium">{label}</div>
          <div className="text-muted-foreground">{lastSeen}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
